import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Filter, Search, Plus, Edit, X } from 'lucide-react';
import api from '../services/api';

const AlertsModule = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ severity: '', status: '', type: '' });
  const [search, setSearch] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    related_id: '',
    related_type: 'Asset',
    type: 'Leakage',
    severity: 'Minor',
    description: '',
    notes: ''
  });
  
  const [editFormData, setEditFormData] = useState({
    status: '',
    assigned_to: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [alertsRes, assetsRes] = await Promise.all([
        api.alerts.getAll(),
        api.assets.getAll()
      ]);
      
      setAlerts(alertsRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.severity) params.append('severity', filter.severity);
      if (filter.status) params.append('status', filter.status);
      if (filter.type) params.append('type', filter.type);
      
      const queryParams = {};
      params.forEach((value, key) => { queryParams[key] = value; });
      const response = await api.alerts.getAll(queryParams);
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchAlerts();
  };

  const handleAddAlert = async () => {
    try {
      await api.alerts.create(formData);
      setShowAddModal(false);
      setFormData({
        related_id: '',
        related_type: 'Asset',
        type: 'Leakage',
        severity: 'Minor',
        description: '',
        notes: ''
      });
      fetchAlerts();
    } catch (error) {
      console.error('Failed to create alert:', error);
      alert('Failed to create alert');
    }
  };

  const handleEditAlert = (alert) => {
    setSelectedAlert(alert);
    setEditFormData({
      status: alert.status,
      assigned_to: alert.assigned_to || '',
      notes: alert.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateAlert = async () => {
    try {
      await api.alerts.update(selectedAlert.id, editFormData);
      setShowEditModal(false);
      setSelectedAlert(null);
      fetchAlerts();
    } catch (error) {
      console.error('Failed to update alert:', error);
      alert('Failed to update alert');
    }
  };

  const canEdit = () => {
    return user && ['Minister', 'DeptHead', 'FieldOfficer'].includes(user.role);
  };

  const canCreate = () => {
    return user && ['Minister', 'DeptHead', 'FieldOfficer'].includes(user.role);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'Major': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Minor': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'Assigned': 'bg-purple-100 text-purple-800',
      'InProgress': 'bg-yellow-100 text-yellow-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredAlerts = alerts.filter(alert => 
    alert.description.toLowerCase().includes(search.toLowerCase()) ||
    alert.type.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="alerts-module">
      {/* Filters */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter size={20} />
              Filters & Search
            </CardTitle>
            {canCreate() && (
              <Button onClick={() => setShowAddModal(true)} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
                <Plus size={16} className="mr-2" />
                Add Alert
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Severity</label>
              <select
                value={filter.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="w-full bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white rounded px-3 py-2"
              >
                <option value="">All</option>
                <option value="Critical">Critical</option>
                <option value="Major">Major</option>
                <option value="Minor">Minor</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Status</label>
              <select
                value={filter.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white rounded px-3 py-2"
              >
                <option value="">All</option>
                <option value="New">New</option>
                <option value="Assigned">Assigned</option>
                <option value="InProgress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Type</label>
              <select
                value={filter.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white rounded px-3 py-2"
              >
                <option value="">All</option>
                <option value="Leakage">Leakage</option>
                <option value="QualityBreach">Quality Breach</option>
                <option value="SupplyDisruption">Supply Disruption</option>
                <option value="Equipment">Equipment</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={applyFilters} className="w-full">
                Apply Filters
              </Button>
            </div>
          </div>
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search alerts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle size={20} />
              Alerts & Incidents ({filteredAlerts.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAlerts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No alerts found</p>
              ) : (
                filteredAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${
                            getSeverityColor(alert.severity)
                          }`}>
                            {alert.severity}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            getStatusBadge(alert.status)
                          }`}>
                            {alert.status}
                          </span>
                          <span className="text-xs text-gray-500">{alert.type}</span>
                        </div>
                        <p className="text-sm text-gray-300 font-medium mb-1">{alert.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>ID: {alert.id}</span>
                          <span>Raised: {new Date(alert.raised_at).toLocaleString()}</span>
                          {alert.assigned_to && <span>Assigned to: {alert.assigned_to}</span>}
                        </div>
                        {alert.notes && (
                          <p className="text-xs text-gray-400 mt-2 italic">{alert.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {canEdit() && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleEditAlert(alert)}
                            className="text-white border-slate-500 bg-slate-700/50 hover:bg-slate-600"
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Alert Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Alert</DialogTitle>
            <DialogDescription>
              Report a new incident or issue in the water management system.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="related_id">Related Asset</Label>
              <Select 
                value={formData.related_id}
                onValueChange={(value) => setFormData({...formData, related_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an asset" />
                </SelectTrigger>
                <SelectContent>
                  {assets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Alert Type</Label>
              <Select 
                value={formData.type}
                onValueChange={(value) => setFormData({...formData, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Leakage">Leakage</SelectItem>
                  <SelectItem value="QualityBreach">Quality Breach</SelectItem>
                  <SelectItem value="SupplyDisruption">Supply Disruption</SelectItem>
                  <SelectItem value="Equipment">Equipment</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity">Severity</Label>
              <Select 
                value={formData.severity}
                onValueChange={(value) => setFormData({...formData, severity: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Critical">Critical</SelectItem>
                  <SelectItem value="Major">Major</SelectItem>
                  <SelectItem value="Minor">Minor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the issue..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional information..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAlert} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
              Create Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Alert Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Alert</DialogTitle>
            <DialogDescription>
              Modify alert status and assignment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={editFormData.status}
                onValueChange={(value) => setEditFormData({...editFormData, status: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Assigned">Assigned</SelectItem>
                  <SelectItem value="InProgress">In Progress</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="assigned_to">Assigned To (Optional)</Label>
              <Input
                id="assigned_to"
                value={editFormData.assigned_to}
                onChange={(e) => setEditFormData({...editFormData, assigned_to: e.target.value})}
                placeholder="User ID"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                placeholder="Update notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAlert} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
              Update Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertsModule;
